import type { FloeMcpConfig } from "./types";

/** Serialized shape accepted by floe-api. Never store this on SDKConfig. */
export interface SupportMcpRequestConfig {
  serverUrl: string;
  bearerToken: string;
  allowedTools: string[];
}

export const SUPPORT_MCP_UNAVAILABLE_WARNING =
  "[OnboardingSDK] Support data tools are unavailable; continuing without them.";

const MAX_SERVER_URL_LENGTH = 2048;
// Keep in lockstep with floe-api and pipecat-server. A client-side success
// that the next hop rejects is worse than one consistent fail-open boundary.
const MAX_BEARER_TOKEN_LENGTH = 8_192;
const MAX_ALLOWED_TOOLS = 20;
const MCP_TOOL_NAME = /^[A-Za-z0-9_.-]{1,128}$/;
const SAFE_HOSTNAME =
  /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)*[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;
const IPV4_LITERAL = /^(?:\d{1,3}\.){3}\d{1,3}$/;
// RFC 6750 token68. Keeping the accepted alphabet narrow and consistent at
// every hop also makes credential redaction deterministic in logs/Sentry.
const BEARER_TOKEN = new RegExp("^[-A-Za-z0-9._~+/]+=*$");
export const SUPPORT_MCP_TOKEN_TIMEOUT_MS = 5_000;

function hasControlCharacters(value: string): boolean {
  for (const character of value) {
    const codePoint = character.codePointAt(0);
    if (codePoint !== undefined && (codePoint <= 31 || codePoint === 127)) {
      return true;
    }
  }
  return false;
}

function isIpLiteral(hostname: string): boolean {
  const unbracketed = hostname.replace(/^\[|\]$/g, "");
  // WHATWG URL parsing canonicalizes IPv4 variants (integer, octal, and hex)
  // to dotted decimal. A colon in the unbracketed value identifies IPv6; it
  // can never occur in a syntactically valid DNS hostname.
  return IPV4_LITERAL.test(unbracketed) || unbracketed.includes(":");
}

async function resolveBearerTokenWithTimeout(
  provider: FloeMcpConfig["getBearerToken"],
): Promise<unknown> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_resolve, reject) => {
    timeoutId = setTimeout(
      () => reject(new Error("support_mcp_token_timeout")),
      SUPPORT_MCP_TOKEN_TIMEOUT_MS,
    );
  });

  try {
    // Start inside a promise so synchronous provider exceptions take the same
    // constant, fail-open path as rejected async providers.
    return await Promise.race([Promise.resolve().then(provider), timeout]);
  } finally {
    if (timeoutId !== undefined) clearTimeout(timeoutId);
  }
}

/**
 * Resolve a support MCP credential at the last responsible moment.
 *
 * This helper intentionally fails open: MCP is an optional data source, so a
 * stale login or a malformed optional config must not take ordinary support
 * chat down with it. The warning is constant and never includes config,
 * provider errors, or credential material.
 */
export async function resolveSupportMcpForSession(
  config: FloeMcpConfig | undefined,
  supportMode: boolean,
  warn: (message: string) => void,
): Promise<SupportMcpRequestConfig | undefined> {
  // Checking the mode first is load-bearing: proactive reconnects must not
  // invoke a host auth callback, even when an MCP config was supplied.
  if (!supportMode || config === undefined) return undefined;

  const failOpen = (): undefined => {
    warn(SUPPORT_MCP_UNAVAILABLE_WARNING);
    return undefined;
  };

  if (
    typeof config !== "object" ||
    config === null ||
    typeof config.serverUrl !== "string" ||
    typeof config.getBearerToken !== "function" ||
    !Array.isArray(config.allowedTools)
  ) {
    return failOpen();
  }

  const serverUrl = config.serverUrl;
  if (
    !serverUrl ||
    serverUrl.length > MAX_SERVER_URL_LENGTH ||
    serverUrl !== serverUrl.trim() ||
    hasControlCharacters(serverUrl)
  ) {
    return failOpen();
  }

  try {
    const parsed = new URL(serverUrl);
    const hostname = parsed.hostname.toLowerCase();
    if (
      parsed.protocol !== "https:" ||
      parsed.username !== "" ||
      parsed.password !== "" ||
      parsed.search !== "" ||
      parsed.hash !== "" ||
      parsed.port !== "" ||
      isIpLiteral(hostname) ||
      !SAFE_HOSTNAME.test(hostname)
    ) {
      return failOpen();
    }
  } catch {
    return failOpen();
  }

  if (
    config.allowedTools.length === 0 ||
    config.allowedTools.length > MAX_ALLOWED_TOOLS ||
    config.allowedTools.some(
      (name) => typeof name !== "string" || !MCP_TOOL_NAME.test(name),
    ) ||
    new Set(config.allowedTools).size !== config.allowedTools.length
  ) {
    return failOpen();
  }

  let bearerToken: unknown;
  try {
    // A host callback may refresh over the network. Bound it so an optional
    // data integration can never leave ordinary support waiting forever.
    bearerToken = await resolveBearerTokenWithTimeout(config.getBearerToken);
  } catch {
    return failOpen();
  }

  if (
    typeof bearerToken !== "string" ||
    bearerToken.length === 0 ||
    bearerToken.length > MAX_BEARER_TOKEN_LENGTH ||
    bearerToken !== bearerToken.trim() ||
    /^Bearer\s/i.test(bearerToken) ||
    !BEARER_TOKEN.test(bearerToken)
  ) {
    return failOpen();
  }

  return {
    serverUrl,
    bearerToken,
    allowedTools: [...config.allowedTools],
  };
}
